import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./MyInformation.scss";
import "../../fonts/fonts.css"
import { categories, myInfoFunctionURL } from "../../Constants";

const SENSITIVE_FIELDS = [
  "Passwort",
  "PIN",
  "Sperrcode",
  "ZugangsDaten",
  "Sicherheitsfrage",
  "Verifizierungscode",
  "Benutzername",
];

const FIELD_LABELS: Record<string, string> = {
  Vorname: "Vorname",
  Nachname: "Nachname",
  Email: "E-Mail",
  ConfirmedEmail: "Bestätigte E-Mail",
  Geburtsdatum: "Geburtsdatum",
  HandyNummer: "Handynummer",
  Gewicht: "Gewicht (kg)",
  Groesse: "Größe (cm)",
  TokenID: "Prozessnummer",
  Passwort: "Passwort",
  PIN: "PIN",
  Sperrcode: "Sperrcode",
  ZugangsDaten: "Zugangsdaten",
  Sicherheitsfrage: "Sicherheitsfrage",
  Verifizierungscode: "Verifizierungscode",
  Benutzername: "Benutzername",
};

const SESSION_SECONDS = 300;
const SENSITIVE_SECONDS = 60;

export default function MyInformation() {
  const { tokenID } = useParams();
  const [step, setStep] = useState<"pin" | "loading" | "show" | "error" | "expired">("pin");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [userData, setUserData] = useState<Record<string, string | undefined> | null>(null);
  const [processName, setProcessName] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [sessionTimer, setSessionTimer] = useState(SESSION_SECONDS);

  // per-sensitive-field reveal timers
  const [revealed, setRevealed] = useState<Record<string, number>>({});
  const revealTimers = useRef<Record<string, number>>({}); // for clearing intervals

  // Session expiry
  useEffect(() => {
    if (step !== "show") return;
    setSessionTimer(SESSION_SECONDS);
    const intv = setInterval(() => {
      setSessionTimer((t) => {
        if (t <= 1) {
          clearInterval(intv);
          setStep("expired");
          setUserData(null);
          setRevealed({});
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intv);
  }, [step]);

  // Per-sensitive field timers
  useEffect(() => {
    // Clean up timers when hiding data
    return () => {
      Object.values(revealTimers.current).forEach(clearInterval);
      revealTimers.current = {};
    }
  }, [step]);

  useEffect(() => {
    if (step !== "show") return;
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        setUserData(null);
        setRevealed({});
        setStep("expired");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [step]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep("loading");
    setError("");
    setUserData(null);
    setRevealed({});

    try {
      const res = await axios.post(
        myInfoFunctionURL!,
        { tokenID, pin },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );
      setUserData(res.data);
      setProcessName(categories[res.data.ProcessID]);
      setStatus(res.data.Status);
      setStep("show");
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setStep("error");
        setError(
          "Zu viele Versuche. Die weitere Nutzung ist für eine Stunde gesperrt."
        );
      } else {
        setStep("error");
        setError(
          err?.response?.data?.message ||
          err?.response?.data ||
          "Fehler bei der Überprüfung. Bitte versuchen Sie es erneut."
        );
      }
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "Neu":
        return { backgroundColor: "#007bff", color: "#fff", padding: "10px 20px", borderRadius: "25px", display: "inline-block" };
      case "In Bearbeitung":
        return { backgroundColor: "#fd7e14", color: "#fff", padding: "10px 20px", borderRadius: "25px", display: "inline-block" };
      case "Abgeschlossen":
        return { backgroundColor: "#28a745", color: "#fff", padding: "10px 20px", borderRadius: "25px", display: "inline-block" };
      default:
        return { backgroundColor: "#6c757d", color: "#fff", padding: "10px 20px", borderRadius: "25px", display: "inline-block" };
    }
  };

  // Handle reveal per field
  function handleReveal(field: string) {
    if (revealed[field]) return;
    setRevealed((prev) => ({ ...prev, [field]: SENSITIVE_SECONDS }));
    const timer = setInterval(() => {
      setRevealed((prev) => {
        const next = { ...prev };
        if (next[field] <= 1) {
          clearInterval(timer);
          delete next[field];
        } else {
          next[field] = next[field] - 1;
        }
        return next;
      });
    }, 1000);
    revealTimers.current[field] = timer as unknown as number;
  }

  // Reset to PIN form
  function handleReset() {
    setStep("pin");
    setPin("");
    setError("");
    setUserData(null);
    setRevealed({});
  }

  return (
    <>
      {step === "pin" && (
        <>
          <p style={{ border: "1px solid black", backgroundColor: "#f0f0f0", padding: "15px", borderRadius: "5px", textAlign: "center", marginBottom: "20px", width: "87%" }}>
            Bitte geben Sie Ihren 4-stellige-PIN-Code ein, den Sie für diesen Vorgang erstellt haben
          </p>
          <form className="pin-step-form" onSubmit={handleSubmit}>
            <label>Ihre 4-stellige-PIN</label>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{4}"
              maxLength={4}
              required
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
              style={{ padding: "12px", fontSize: "1.2rem", textAlign: "center", borderRadius: 8, border: "1px solid #CCC" }}
            />

            <button className="back-button" style={{ marginTop: 24, alignSelf: "center" }}>Absenden</button>
          </form>
          {error && <div className="error-message">{error}</div>}
        </>
      )}

      {step === "loading" && (
        <div className="status-message info">Überprüfung läuft...</div>
      )}

      {step === "error" && (
        <>
          <div className="error-message">{error}</div>
          <button className="back-button" onClick={handleReset}>Schließen</button>
        </>
      )}

      {step === "expired" && (
        <>
          <div className="error-message">Sitzung abgelaufen. Bitte geben Sie Ihren PIN erneut ein.</div>
          <button className="back-button" onClick={handleReset}>Schließen</button>
        </>
      )}

      {step === "show" && userData && (
        <>
          <div className="status-message success">Ihre persönlichen Daten</div>
          <h2>{processName}</h2>
          {status && <h4 style={getStatusStyle(status)}>{status}</h4>}
          <div className={`session-timer${sessionTimer <= 60 ? " warning" : ""}`}>
            Verbleibende Zeit: {Math.floor(sessionTimer / 60)}:{(sessionTimer % 60).toString().padStart(2, '0')}
          </div>
          <div className="data-container">
            {Object.entries(userData).map(([key, value]) => {
              if (!value) return null;
              if (key === "ProcessID" || key === "Status") return null;
              const label = FIELD_LABELS[key] || key;
              if (SENSITIVE_FIELDS.includes(label)) {
                return (
                  <div className="data-row" key={key}>
                    <span className="label">{label}:</span>
                    <div className="sensitive-value" style={{ width: "100%" }}>
                      {!revealed[key] ? (
                        <button
                          type="button"
                          onClick={() => handleReveal(key)}
                          style={{
                            color: "#2196F3",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            fontSize: "1rem"
                          }}
                        >
                          Anzeigen
                        </button>
                      ) : (
                        <div>
                          <div style={{
                            background: "#f5f5f5",
                            padding: "8px",
                            borderRadius: "4px",
                            fontFamily: "monospace",
                            fontSize: "0.95rem",
                            marginBottom: 4,
                            display: "inline-block"
                          }}>{value}</div>
                          <span style={{ color: "#ff9800", fontSize: "0.85em" }}>
                            (noch {revealed[key]} Sekunden sichtbar)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="data-row" key={key}>
                    <span className="label">{label}:</span>
                    <span className="value">{value}</span>
                  </div>
                );
              }
            })}
          </div>
          <small style={{ color: "red" }}>Sie sehen vertrauliche Daten. Bitte schließen Sie das Fenster, wenn Sie fertig sind, und verwenden Sie dieses Gerät nicht in öffentlichen/unsicheren Umgebungen.</small>
          <button className="back-button" onClick={handleReset}>Schließen</button>
        </>
      )}
    </>
  );
}